<template>
  <b-container>
    <b-row>
      <b-col cols="12">
        <b-tabs v-model="selectedTab" no-fade lazy align="center">
          <b-tab v-for="(locale, index) in locales" :key="locale.code" :active="index === 0" :title="locale.label"
            class="mt-3 border">
            <component :is="tabComponent" :locale="locale.code" @content-updated="onContentUpdated" />
          </b-tab>
        </b-tabs>
      </b-col>
    </b-row>

    <!-- BUTTONS -->
    <b-row class="pt-3 mb-1 d-flex justify-content-between justify-content-sm-end">
      <b-col v-if="showWhatsAppButton" cols="12" sm="auto" class="mb-2 mb-sm-0">
        <b-button block variant="secondary" :disabled="!contentWhatsAppText"
          @click="copyToClipboard(contentWhatsAppText)">
          Copia per WhatsApp
        </b-button>
      </b-col>
      <b-col v-if="markAsSentButtonVisible" cols="12" sm="auto" class="mb-2 mb-sm-0">
        <b-button block @click="markEmailAsSent" variant="secondary">
          Marca com a enviat
        </b-button>
      </b-col>
      <b-col cols="12" sm="auto" class="send-button">
        <b-button block :disabled="!contentOwnerEmail" variant="primary" @click="sendEmail">
          Envia
        </b-button>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import { BContainer, BRow, BCol, BTabs, BTab, BButton } from "bootstrap-vue";
import { notifyError, copyToClipboard, getAdultsText, getBabiesText, getChildrensText } from "@/utils/methods";
import axios from "@/axios";

// Lazy load components
const componentImports = {
  'booking-confirmation': () => import("@/views/bookings/booking/components/notifications/OwnerEmailNotificationFormBookingConfirmation.vue"),
  'booking-modification': () => import("@/views/bookings/booking/components/notifications/OwnerEmailNotificationFormBookingModification.vue"),
  'booking-cancellation': () => import("@/views/bookings/booking/components/notifications/OwnerEmailNotificationFormBookingCancellation.vue"),
  'checkin-information': () => import("@/views/bookings/booking/components/notifications/OwnerEmailNotificationFormCheckinInformation.vue"),
  'guests-report': () => import("@/views/bookings/booking/components/notifications/OwnerEmailNotificationFormGuestsReport.vue"),
};

// Notification configuration
const NOTIFICATION_CONFIG = {
  "booking-confirmation": {
    urlPath: "booking-confirmation",
    sendLogType: "OWNER_BOOKING_CONFIRMATION_SENT",
    markedAsSentLogType: "OWNER_BOOKING_CONFIRMATION_MARKED_AS_SENT",
    updateStatsProps: { ownerBookingConfirmationSent: true, ownerBookingConfirmationSentDate: new Date() },
    showWhatsApp: false,
    showMarkAsSent: true,
  },
  "booking-modification": {
    urlPath: "booking-modification",
    sendLogType: "OWNER_BOOKING_MODIFICATION_SENT",
    markedAsSentLogType: "OWNER_BOOKING_MODIFICATION_MARKED_AS_SENT",
    updateStatsProps: null,
    showWhatsApp: false,
  },
  "booking-cancellation": {
    urlPath: "booking-cancellation",
    sendLogType: "OWNER_BOOKING_CANCELLATION_SENT",
    markedAsSentLogType: "OWNER_BOOKING_CANCELLATION_MARKED_AS_SENT",
    updateStatsProps: null,
    showWhatsApp: false,
    useNotificationsEndpoint: true,
  },
  "checkin-information": {
    urlPath: "checkin-information",
    sendLogType: "OWNER_CHECKIN_INFORMATION_SENT",
    markedAsSentLogType: "OWNER_CHECKIN_INFORMATION_MARKED_AS_SENT",
    updateStatsProps: { ownerArrivalInformationSent: true, ownerArrivalInformationSentDate: new Date() },
    showWhatsApp: true,
    showMarkAsSent: true,
  },
  "guests-report": {
    urlPath: "guests-report",
    sendLogType: "OWNER_GUESTS_REPORT_SENT",
    markedAsSentLogType: "OWNER_GUESTS_REPORT_MARKED_AS_SENT",
    updateStatsProps: null,
    showWhatsApp: false,
  },
};

// Language locales configuration
const LOCALES = [
  { code: 'ca-ES', label: 'Català', shortCode: 'ca' },
  { code: 'es-ES', label: 'Castellano', shortCode: 'es' },
  { code: 'en-GB', label: 'English', shortCode: 'en' },
  { code: 'de-DE', label: 'Deutsch', shortCode: 'de' },
];

export default {
  props: {
    notificationType: {
      type: String,
      required: true,
      validator(value) {
        return Object.keys(NOTIFICATION_CONFIG).includes(value);
      },
    },
  },
  components: {
    BContainer,
    BRow,
    BCol,
    BTabs,
    BTab,
    BButton,
  },
  data() {
    return {
      selectedTab: 0,
      content: null,
      locales: LOCALES,
      copyToClipboard,
    };
  },
  computed: {
    booking() {
      return this.$store.getters["booking/booking"];
    },
    stats() {
      return this.$store.getters["booking/stats"];
    },
    owner() {
      return this.$store.getters["booking/owner"];
    },
    contentOwnerEmail() {
      return this.content?.ownerEmail || null;
    },
    notificationConfig() {
      return NOTIFICATION_CONFIG[this.notificationType] || {};
    },
    tabComponent() {
      return componentImports[this.notificationType] || null;
    },
    postUrl() {
      const urlPath = this.notificationConfig.urlPath;
      const { locale = "ca-ES" } = this.content || {};

      if (!urlPath) return null;

      const baseUrl = this.notificationConfig.useNotificationsEndpoint ? '/notifications/owner' : '/mail/owner';
      return `${baseUrl}/${urlPath}${this.notificationConfig.useNotificationsEndpoint ? '' : `/${locale}`}`;
    },
    sendLogType() {
      return this.notificationConfig.sendLogType;
    },
    markedAsSentLogType() {
      return this.notificationConfig.markedAsSentLogType;
    },
    updateStatsProps() {
      return this.notificationConfig.updateStatsProps;
    },
    markAsSentButtonVisible() {
      return this.notificationConfig.showMarkAsSent || false;
    },
    showWhatsAppButton() {
      return this.notificationConfig.showWhatsApp || false;
    },
    contentWhatsAppText() {
      if (this.notificationType === 'checkin-information') {
        return this.checkinInformationWhatsAppText || null;
      }
      return null;
    },
    checkinInformationWhatsAppText() {
      let text = [];
      const {
        locale,
        ownerName,
        clientName,
        accommodationName,
        localizator,
        checkinDay,
        checkinTime,
        checkoutDay,
        checkoutTime,
        adults,
        adultsAges,
        children,
        babies,
        selfCheckin,
        lockboxCode,
        requests,
        payments,
      } = this.content || {};

      // Intro
      const introText = [];
      introText.push(`Hola${ownerName ? " " + ownerName : ""} 👋\r\n`);
      introText.push(
        this.$t("emails.ownerInstructions.intro", locale, {
          clientName,
          localizator,
          accommodationName,
        })
      );
      text.push(introText.join("\r\n"));

      // Times
      const timesText = [];
      timesText.push(
        `*📆 ${this.$t("emails.ownerInstructions.timesTitle", locale)}*\r\n`
      );
      timesText.push(
        `- ${this.$t("Checkin", locale)}: ${checkinDay}${
          checkinTime ? " a les " + checkinTime : ""
        }`
      );
      timesText.push(
        `- ${this.$t("Checkout", locale)}: ${checkoutDay}${
          checkoutTime ? " a les " + checkoutTime : ""
        }`
      );
      timesText.push(
        `\r\n_${this.$t("emails.ownerInstructions.timesNote", locale)}_`
      );
      text.push(timesText.join("\r\n"));

      // Guests
      const guestsText = [];
      guestsText.push(
        `*👨‍👩‍👧‍👦 ${this.$t("emails.ownerInstructions.guestsTitle", locale)}*\r\n`
      );
      if (adults) {
        guestsText.push(
          `- ${getAdultsText(adults, locale)}${
            adultsAges ? " (" + adultsAges + ")" : ""
          }`
        );
      }
      if (children) {
        guestsText.push(`- ${getChildrensText(children, locale)}`);
      }
      if (babies) {
        guestsText.push(`- ${getBabiesText(babies, locale)}`);
      }
      text.push(guestsText.join("\r\n"));

      // Keys
      const keysText = [];
      keysText.push(
        `*🔑 ${this.$t("emails.ownerInstructions.keysTitle", locale)}*\r\n`
      );
      if (selfCheckin) {
        keysText.push(
          `- ${this.$t("emails.ownerInstructions.keysSelfCheckin", locale)}${
            lockboxCode
              ? " (" +
                this.$t("emails.ownerInstructions.keysLockboxCode", locale) +
                ": " +
                lockboxCode +
                ")"
              : ""
          }`
        );
      } else {
        keysText.push(
          `- ${this.$t("emails.ownerInstructions.keysOwnerCheckin", locale)}`
        );
      }
      text.push(keysText.join("\r\n"));

      // Requests
      if (requests?.length) {
        const requestsText = [];
        requestsText.push(
          `*🙋 ${this.$t(
            "emails.ownerInstructions.requestsTitle",
            locale
          )}*\r\n`
        );
        requests.forEach((request) => {
          requestsText.push(`- ${request.title}`);
        });
        text.push(requestsText.join("\r\n"));
      }

      // Payments
      if (payments?.length) {
        const paymentsText = [];
        paymentsText.push(
          `*💳 ${this.$t(
            "emails.ownerInstructions.paymentsTitle",
            locale
          )}*\r\n`
        );
        payments.forEach((payment) => {
          const { title, price, badgeText } = payment || {};
          paymentsText.push(
            `- ${title}: ${price}${badgeText ? " (_" + badgeText + "_)" : ""}`
          );
        });
        text.push(paymentsText.join("\r\n"));
      }

      // Result text
      text = text.length ? text.join("\r\n\r\n") : null;

      return text;
    },
  },
  mounted() {
    setTimeout(() => {
      this.initTab();
    }, 50);
  },
  methods: {
    initTab() {
      if (!this.owner) return;

      const language = this.owner?.language || "ca";
      const tabIndex = LOCALES.findIndex(locale => locale.shortCode === language);
      this.selectedTab = tabIndex !== -1 ? tabIndex : 0;
    },
    onContentUpdated(content) {
      this.content = content;
    },
    async sendEmail() {
      if (!this.contentOwnerEmail) {
        notifyError("Error", "El propietari d'aquesta reserva no disposa de correu electrònic");
        this.$emit("send-error");
        return;
      }

      if (!this.postUrl) {
        notifyError("Error", "No hi ha cap url definida per aquest tipus de notificació");
        this.$emit("send-error");
        return;
      }

      this.$store.dispatch("app/setLoading", true);

      try {
        await axios.post(this.postUrl, this.content);

        // Save the log entry, if needed
        if (this.sendLogType) {
          await this.$store.dispatch("booking/addLog", {
            booking: this.booking["@id"],
            type: this.sendLogType,
            data: this.content,
          });
        }

        this.$emit("sent");
      } catch (error) {
        this.$emit("send-error");
      } finally {
        this.$store.dispatch("app/setLoading", false);
      }
    },
    async markEmailAsSent() {
      this.$store.dispatch("app/setLoading", true);

      try {
        // Update booking stats
        if (this.updateStatsProps !== null) {
          if (!this.stats) {
            throw new Error("No s'han pogut recuperar les estadístiques de la reserva per actualitzar-les");
          }

          await this.$store.dispatch("booking/updateStats", {
            uuid: this.stats.uuid,
            ...this.updateStatsProps,
          });
        }

        // Save the log entry, if needed
        if (this.markedAsSentLogType) {
          await this.$store.dispatch("booking/addLog", {
            booking: this.booking["@id"],
            type: this.markedAsSentLogType,
          });
        } else {
          throw new Error("Unsupported log type");
        }

        this.$emit("marked-as-sent");
      } catch (error) {
        notifyError("Error", error.message);
        this.$emit("mark-as-sent-error");
      } finally {
        this.$store.dispatch("app/setLoading", false);
      }
    },
  },
};
</script>
